import { useCallback, useEffect, useState } from 'react'

import NotificationsService from '@/services/notifications.service'
import { useAuthStore } from '@/stores/authStore'
import type { NotificationItem } from '@/types'

const POLL_INTERVAL_MS = 30_000

export function useNotifications() {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [count, setCount] = useState(0)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [loadingCount, setLoadingCount] = useState(false)
  const [loadingItems, setLoadingItems] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) {
      setCount(0)
      return
    }

    setLoadingCount(true)
    try {
      const nextCount = await NotificationsService.getUnreadCount()
      setCount(nextCount)
    } catch {
      setCount(0)
    } finally {
      setLoadingCount(false)
    }
  }, [isAuthenticated])

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setItems([])
      setError(null)
      return
    }

    setLoadingItems(true)
    setError(null)
    try {
      const notifications = await NotificationsService.getAll(20)
      setItems(notifications)
      setCount(notifications.filter((item) => !item.read_at).length)
    } catch {
      setError('Nao foi possivel carregar as notificacoes.')
    } finally {
      setLoadingItems(false)
    }
  }, [isAuthenticated])

  const markAsRead = useCallback(async (notificationId: string) => {
    await NotificationsService.markAsRead(notificationId)
    setItems((current) =>
      current.map((item) =>
        item.id === notificationId && !item.read_at
          ? { ...item, read_at: new Date().toISOString() }
          : item,
      ),
    )
    setCount((current) => Math.max(0, current - 1))
  }, [])

  const markAllAsRead = useCallback(async () => {
    await NotificationsService.markAllAsRead()
    const now = new Date().toISOString()
    setItems((current) => current.map((item) => ({ ...item, read_at: item.read_at ?? now })))
    setCount(0)
  }, [])

  useEffect(() => {
    if (!isAuthenticated) {
      setCount(0)
      setItems([])
      setError(null)
      return
    }

    fetchUnreadCount().catch(() => undefined)
    const interval = window.setInterval(() => {
      fetchUnreadCount().catch(() => undefined)
    }, POLL_INTERVAL_MS)

    return () => window.clearInterval(interval)
  }, [fetchUnreadCount, isAuthenticated])

  return {
    count,
    items,
    loadingCount,
    loadingItems,
    error,
    fetchUnreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  }
}
